/**
 * Customer approval stamps on the frontend — parsing, display rows, and the
 * approve gate. The API side is fieldiq-api `lib/approval-stamp.ts` (api#709).
 *
 * The server PDF renders the frozen stamp itself, from the same frozen copy.
 * This module does NOT mirror that renderer and must not grow into one; it only
 * turns the payload into label/value pairs for `FieldTicketDisplay`.
 */
import type {
  ReviewStamp,
  ReviewStampFrozen,
  ReviewStampLive,
  StampSignoffRole,
  StampedFileRef,
} from '../api/public';
import { formatCalendarDate } from './calendar-date';

/** What `FieldTicketDisplay` renders for a stamp. Source-agnostic on purpose. */
export interface TicketStampDisplay {
  operatorName: string;
  /** A validated hex colour, or null for the default text colour. */
  operatorColor: string | null;
  /**
   * `kind: 'signature'` marks the row whose value is the word "Signed" — the
   * one row a caller may draw the signature image into. It is set only when
   * the stamp has a signature, so a caller with no image still prints `value`.
   */
  rows: { label: string; value: string; kind?: 'signature' }[];
}

/** Server-side limits, so an overlong entry is stopped in the input rather than as a 400. */
export const STAMP_VALUE_MAX_LENGTH = 500;
export const STAMP_AFE_MAX_LENGTH = 100;
export const STAMP_SIGNER_NAME_MAX_LENGTH = 200;

/**
 * A colour safe to put in a style attribute. Same pattern the API applies, and
 * applied again here because `GET /tickets/:id` hands back the raw column
 * rather than the review route's already-validated projection.
 */
export function safeStampColor(value: unknown): string | null {
  return typeof value === 'string' && /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(value) ? value : null;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const ROLES = new Set<StampSignoffRole>(['signer_name', 'signature', 'signed_at']);

/**
 * `data.stamp` from GET /ticket-approval/review/:token, or null.
 *
 * Anything that is not recognisably one of the two shapes is null — the page
 * then behaves as it does for a customer with no stamp. A stamp that cannot be
 * read must not become an approve button gated on rows nobody can see.
 */
export function parseReviewStamp(raw: unknown): ReviewStamp | null {
  if (!isObj(raw) || !Array.isArray(raw.rows) || typeof raw.operator_name !== 'string') return null;
  const signoff = Array.isArray(raw.signoff) ? raw.signoff.filter((s: any) => isObj(s) && ROLES.has(s.role)) : [];

  if (raw.frozen === true) {
    return {
      frozen: true,
      template_id: str(raw.template_id),
      template_version: Number(raw.template_version),
      operator_name: raw.operator_name,
      operator_color: safeStampColor(raw.operator_color),
      rows: raw.rows.filter(isObj).map((r: any) => ({ label: str(r.label), value: str(r.value) })),
      signoff: signoff.map((s: any) => ({ label: str(s.label), role: s.role })),
      signer_name: str(raw.signer_name),
      signed_at: typeof raw.signed_at === 'string' ? raw.signed_at : null,
      signed_on: typeof raw.signed_on === 'string' ? raw.signed_on : null,
      has_signature: raw.has_signature === true,
    };
  }

  if (raw.frozen === false) {
    return {
      frozen: false,
      template_id: str(raw.template_id),
      template_version: Number(raw.template_version),
      operator_name: raw.operator_name,
      operator_color: safeStampColor(raw.operator_color),
      rows: raw.rows
        .filter((r: any) => isObj(r) && typeof r.field_id === 'string')
        .map((r: any) => ({
          field_id: r.field_id,
          label: str(r.label),
          field_type: str(r.field_type),
          required: r.required === true,
          placeholder: typeof r.placeholder === 'string' ? r.placeholder : null,
          help_text: typeof r.help_text === 'string' ? r.help_text : null,
          choices: Array.isArray(r.choices) ? r.choices.map(str) : null,
          binding: typeof r.binding === 'string' ? r.binding : null,
          read_only: r.read_only === true,
          value: str(r.value),
        })),
      signoff: signoff
        .filter((s: any) => typeof s.field_id === 'string')
        .map((s: any) => ({ field_id: s.field_id, label: str(s.label), role: s.role })),
      requires_signer_name: raw.requires_signer_name !== false,
      requires_signature: raw.requires_signature !== false,
    };
  }

  return null;
}

/**
 * The frozen copy as `GET /tickets/:id` returns it — the raw `customer_stamp`
 * column, which carries `signature_key` instead of `has_signature`. Null for a
 * ticket signed without a stamp, or not signed yet.
 */
export function frozenStampFromTicket(ticket: unknown): ReviewStampFrozen | null {
  if (!isObj(ticket)) return null;
  const col = ticket.customer_stamp ?? ticket.customerStamp;
  if (!isObj(col)) return null;
  return parseReviewStamp({
    ...col,
    frozen: true,
    has_signature: col.has_signature === true || (typeof col.signature_key === 'string' && col.signature_key !== ''),
  }) as ReviewStampFrozen | null;
}

/**
 * `data.stamped_file` from GET /ticket-approval/review/:token, or null.
 *
 * Both spellings are read because the API sends both — `stamped_file` with an
 * `uploaded_at`, and a legacy `stampedFile` alias with an `uploadedAt`. The
 * inner key differs between them, which is exactly the drift that renders a
 * date as blank forever, so each is read next to its own outer key rather than
 * as one flat `??` chain that would pair the wrong two.
 *
 * A record with no usable `filename` is null: an upload row whose name cannot be
 * read is not a thing to show the customer as "approved by file", and a blank
 * name would render as a gap under a heading promising a document.
 */
export function parseStampedFileRef(raw: unknown): StampedFileRef | null {
  const snake = isObj(raw) ? raw.stamped_file : null;
  const camel = isObj(raw) ? raw.stampedFile : null;

  const from = (obj: unknown, uploadedKey: 'uploaded_at' | 'uploadedAt'): StampedFileRef | null => {
    if (!isObj(obj)) return null;
    const filename = str(obj.filename).trim();
    if (filename === '') return null;
    const at = obj[uploadedKey];
    return { filename, uploaded_at: typeof at === 'string' && at !== '' ? at : null };
  };

  return from(snake, 'uploaded_at') ?? from(camel, 'uploadedAt');
}

/** An absent value is a dash, never an empty cell. */
const shown = (v: string | null | undefined): string => (v && v.trim() !== '' ? v : '—');

/**
 * The signed stamp as display rows: the data rows, then one row per sign-off
 * role in template order. A template with no signer-name role still gets a
 * "Signed by" row, because every frozen copy has a signer.
 */
export function frozenStampDisplay(stamp: ReviewStampFrozen): TicketStampDisplay {
  const rows: TicketStampDisplay['rows'] = stamp.rows.map((r) => ({ label: r.label, value: shown(r.value) }));
  for (const s of stamp.signoff) {
    if (s.role === 'signer_name') rows.push({ label: s.label, value: shown(stamp.signer_name) });
    else if (s.role === 'signature') {
      rows.push(stamp.has_signature ? { label: s.label, value: 'Signed', kind: 'signature' } : { label: s.label, value: '—' });
    }
    else if (s.role === 'signed_at') rows.push({ label: s.label, value: shown(formatCalendarDate(stamp.signed_on)) });
  }
  if (!stamp.signoff.some((s) => s.role === 'signer_name')) {
    rows.push({ label: 'Signed by', value: shown(stamp.signer_name) });
  }
  return { operatorName: stamp.operator_name, operatorColor: safeStampColor(stamp.operator_color), rows };
}

/** Longest value the server accepts for this row. */
export function stampRowMaxLength(row: { binding: string | null }): number {
  return row.binding === 'ticket.afe_number' ? STAMP_AFE_MAX_LENGTH : STAMP_VALUE_MAX_LENGTH;
}

/**
 * What still has to be filled before Approve may be pressed, by label, in page
 * order. Empty means ready. Read-only rows are never listed: the approver
 * cannot supply them and the server recomputes them anyway.
 */
export function missingStampInputs(
  stamp: ReviewStampLive,
  values: Record<string, string>,
  signerName: string,
  signature: string | null,
): string[] {
  const missing = stamp.rows
    .filter((r) => r.required && !r.read_only && (values[r.field_id] ?? '').trim() === '')
    .map((r) => r.label);
  if (stamp.requires_signer_name && signerName.trim() === '') missing.push(signerNameLabel(stamp));
  if (stamp.requires_signature && !signature) missing.push(signatureLabel(stamp));
  return missing;
}

export function signerNameLabel(stamp: ReviewStampLive): string {
  return stamp.signoff.find((s) => s.role === 'signer_name')?.label || 'Name';
}

export function signatureLabel(stamp: ReviewStampLive): string {
  return stamp.signoff.find((s) => s.role === 'signature')?.label || 'Signature';
}

/**
 * `customer_stamp_file` from `GET /tickets/:id`, for display — or null when the
 * ticket holds no file.
 *
 * Two doors write it (api#995): the customer's magic-link upload, and the
 * office attaching a copy the customer signed elsewhere. The API normalises the
 * record so `source` is always present and the three attach keys are null on a
 * customer copy. A `source` outside those two is kept as null rather than
 * guessed: "the customer sent this" and "the office put this here" are
 * different claims about who vouched for the document.
 *
 * Never carries `r2_key` or `sha256` — nothing on screen may show them.
 */
export interface TicketStampFileInfo {
  filename: string | null;
  uploadedAt: string | null;
  source: 'customer' | 'office' | null;
  attachedByName: string | null;
  attachedAt: string | null;
}

export function parseTicketStampFile(raw: unknown): TicketStampFileInfo | null {
  if (!isObj(raw)) return null;
  const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const source = raw.source === 'customer' || raw.source === 'office' ? raw.source : null;
  return {
    filename: text(raw.filename),
    uploadedAt: text(raw.uploaded_at ?? raw.uploadedAt),
    source,
    attachedByName: text(raw.attached_by_name ?? raw.attachedByName),
    attachedAt: text(raw.attached_at ?? raw.attachedAt),
  };
}
